import { createSlice } from "@reduxjs/toolkit";

const initialTableOrderState: any = {
  tableId: null,
  tableNumber: "",
  peopleCount: 0,
  orders: [],
  billNumber: "",
  status: "Available",
  kotPrintTimes: [],
  paid: 0,
  orderNumber: "", // <-- Add this
  kotDetails: null, // <-- Add this
  // Add more fields as needed
};

const initialState: any = {
  current: initialTableOrderState,
  allTableOrders: [], // [{tableId, tableNumber, ...tableOrder}]
  activeInput: "",
  activeInputId: "",
  isSelected: false,
};

// Helper to find table index by tableId or tableNumber
function findTableIndex(allTableOrders: any[], tableId: any, tableNumber: any) {
  return allTableOrders.findIndex(
    (t) =>
      (tableId && t.tableId === tableId) ||
      (!tableId && t.tableNumber === tableNumber)
  );
}

export const tableOrderSlice = createSlice({
  name: "tableOrder",
  initialState,
  reducers: {
    setTable: (state, action) => {
      // Save current table order before switching (if it has items)
      if (
        state.current &&
        (state.current.orders?.length > 0 || state.current.tableNumber)
      ) {
        const idx = findTableIndex(
          state.allTableOrders,
          state.current.tableId,
          state.current.tableNumber
        );
        if (idx >= 0) {
          state.allTableOrders[idx] = { ...state.current };
        } else {
          state.allTableOrders.push({ ...state.current });
        }
      }
      // Load new table order if exists, else start fresh
      const { tableId, tableNumber } = action.payload;
      const idx = findTableIndex(state.allTableOrders, tableId, tableNumber);
      if (idx >= 0) {
        state.current = { ...state.allTableOrders[idx] };
      } else {
        state.current = { ...initialTableOrderState, ...action.payload };
      }
    },
    addTableOrderItem: (state, action) => {
      const now = new Date();
      const timeString = now.toISOString();
      // Check if item with same itemId and priceHistoryId exists, then increase qty
      const existing = state.current.orders.find(
        (i: any) =>
          i.itemId === action.payload.itemId &&
          i.priceHistoryId === action.payload.priceHistoryId
      );
      if (existing) {
        // Add to existing quantity
        const addQty = action.payload.quantity || 1;
        existing.quantity += addQty;
        existing.finalPrice =
          existing.price * existing.quantity -
          (existing.discount * existing.quantity || 0);
      } else {
        state.current.orders.unshift({
          ...action.payload,
          id: timeString,
          quantity: action.payload.quantity || 1,
          discount: `${action.payload.discount || 0}`,
          finalPrice:
            action.payload.price * (action.payload.quantity || 1) -
            (action.payload.discount * (action.payload.quantity || 1) || 0),
          priceHistoryId: action.payload.priceHistoryId,
        });
      }
    },
    increaseTableOrderItemQty: (state, action) => {
      const { id } = action.payload;
      const item = state.current.orders.find((item: any) => item.id === id);
      if (item) {
        item.quantity += 1;
        item.finalPrice =
          item.price * item.quantity - (item.discount * item.quantity || 0);
      }
    },
    decreaseTableOrderItemQty: (state, action) => {
      const { id } = action.payload;
      const item = state.current.orders.find((item: any) => item.id === id);
      if (item && item.quantity > 1) {
        item.quantity -= 1;
        item.finalPrice =
          item.price * item.quantity - (item.discount * item.quantity || 0);
      }
    },
    removeTableOrderItem: (state, action) => {
      state.current.orders = state.current.orders.filter(
        (item: any) => item.id !== action.payload
      );
    },
    updateTableOrderDiscount: (state, action) => {
      const { id, value } = action.payload;
      const item = state.current.orders.find((item: any) => item.id === id);
      if (item) {
        item.discount = value;
        item.finalPrice =
          item.price * item.quantity - (parseFloat(value) * item.quantity || 0);
      }
    },
    clearTableOrder: (state) => {
      // Remove from allTableOrders as well
      const idx = findTableIndex(
        state.allTableOrders,
        state.current.tableId,
        state.current.tableNumber
      );
      if (idx >= 0) {
        state.allTableOrders.splice(idx, 1);
      }
      state.current = { ...initialTableOrderState };
    },
    setTableOrderBillNumber: (state, action) => {
      state.current.billNumber = action.payload;
    },
    setTableOrderStatus: (state, action) => {
      state.current.status = action.payload;
    },
    addKotPrintTime: (state) => {
      if (!state.current.kotPrintTimes) state.current.kotPrintTimes = [];
      state.current.kotPrintTimes.push(new Date().toISOString());
    },
    // Save current table order to allTableOrders (call after KOT or print)
    saveCurrentTableOrder: (state) => {
      const idx = findTableIndex(
        state.allTableOrders,
        state.current.tableId,
        state.current.tableNumber
      );
      if (idx >= 0) {
        state.allTableOrders[idx] = { ...state.current };
      } else {
        state.allTableOrders.push({ ...state.current });
      }
    },
    // Optionally, clear all table orders (for admin)
    clearAllTableOrders: (state) => {
      state.allTableOrders = [];
    },
    changeQtyDirect: (state, action) => {
      const { id, value } = action.payload;
      const item = state.current.orders.find((item: any) => item.id === id);
      if (item && value > 0) {
        item.quantity = value;
        item.finalPrice =
          item.price * value - (parseFloat(item.discount) * value || 0);
      }
    },
    setPaidAmount: (state, action) => {
      state.current.paid = action.payload;
    },
    setOrderNumber: (state, action) => {
      state.current.orderNumber = action.payload;
    },
    setKotDetails: (state, action) => {
      state.current.kotDetails = action.payload;
    },
    clearKotDetails: (state) => {
      state.current.kotDetails = null;
    },
  },
});

export const {
  setTable,
  addTableOrderItem,
  increaseTableOrderItemQty,
  decreaseTableOrderItemQty,
  removeTableOrderItem,
  updateTableOrderDiscount,
  clearTableOrder,
  setTableOrderBillNumber,
  setTableOrderStatus,
  addKotPrintTime,
  saveCurrentTableOrder,
  clearAllTableOrders,
  changeQtyDirect,
  setPaidAmount,
  setOrderNumber,      // <-- export new actions
  setKotDetails,
  clearKotDetails,
} = tableOrderSlice.actions;

export default tableOrderSlice.reducer;
